import React, { useState, useEffect, useCallback } from 'react';
import api from '../core/api';
import Tooltip from '../components/Tooltip';

// ─── Types ───────────────────────────────────────────────────────────────────

interface PaymentEvent {
  id: string;
  type: string;
  organizationName: string | null;
  buyerName: string | null;
  buyerEmail: string | null;
  buyerPhone: string | null;
  plan: string | null;
  planLabel: string | null;
  billingCycle: string | null;
  amount: number | null;
  currency: string;
  isEarlyOffer: boolean;
  paypalSubscriptionId: string | null;
  trialDaysUsed: number | null;
  contactsUsed: number | null;
  converted: boolean;
  createdAt: string;
}

interface PayPalConfig {
  environment: string;
  clientIdConfigured: boolean;
  clientSecretConfigured: boolean;
  webhookIdConfigured: boolean;
  verifyMode: string;
  ready: boolean;
}

interface Stats {
  pagos: number;
  renovaciones: number;
  cancelaciones: number;
  checkouts: number;
  checkoutsConvertidos: number;
  checkoutsAbandonados: number;
  tasaConversion: number;
  ingresosMes: number;
}

// ─── Meta por tipo de evento ─────────────────────────────────────────────────

const TYPE_META: Record<string, { label: string; icon: string; color: string; badge: string }> = {
  payment_completed: {
    label: 'Compra', icon: 'paid', color: 'text-emerald-500',
    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  },
  subscription_renewed: {
    label: 'Renovación', icon: 'autorenew', color: 'text-indigo-500',
    badge: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  },
  checkout_started: {
    label: 'Entró a pagar', icon: 'shopping_cart', color: 'text-amber-500',
    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  },
  subscription_cancelled: {
    label: 'Cancelación', icon: 'cancel', color: 'text-red-500',
    badge: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  },
  payment_failed: {
    label: 'Pago fallido', icon: 'error', color: 'text-red-500',
    badge: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  },
};
const meta = (t: string) => TYPE_META[t] || { label: t, icon: 'help', color: 'text-slate-400', badge: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' };

const money = (n: number | null) => (n != null ? `$${n.toLocaleString('en-US')}` : '—');

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000), h = Math.floor(diff / 3_600_000), d = Math.floor(diff / 86_400_000);
  if (m < 1) return 'Ahora';
  if (m < 60) return `Hace ${m}m`;
  if (h < 24) return `Hace ${h}h`;
  if (d < 30) return `Hace ${d}d`;
  return new Date(iso).toLocaleDateString('es-DO', { day: 'numeric', month: 'short' });
}

// ─── Main ────────────────────────────────────────────────────────────────────

const Payments: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [events, setEvents] = useState<PaymentEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [paypal, setPaypal] = useState<PayPalConfig | null>(null);
  const [errorCarga, setErrorCarga] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pagos, cfg] = await Promise.allSettled([
        api.get('/billing/payments?limit=200'),
        api.get('/billing/paypal/config'),
      ]);

      if (pagos.status === 'fulfilled' && pagos.value.data?.success) {
        setStats(pagos.value.data.stats);
        setEvents(pagos.value.data.events || []);
        setErrorCarga(null);
      } else {
        const err: any = pagos.status === 'rejected' ? pagos.reason : null;
        setErrorCarga(err?.response?.data?.error || 'No se pudo cargar el historial de pagos.');
      }

      setPaypal(cfg.status === 'fulfilled' && cfg.value.data?.success ? cfg.value.data.data : null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = filter === 'all' ? events : events.filter(e => e.type === filter);

  // Abandonos: entraron a pagar y no completaron
  const abandonados = events.filter(e => e.type === 'checkout_started' && !e.converted);

  const conteos = events.reduce((acc, e) => {
    acc[e.type] = (acc[e.type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-300">
        <span className="material-symbols-outlined animate-spin text-[36px]">progress_activity</span>
      </div>
    );
  }

  return (
    <div className="px-4 pt-6 pb-10 max-w-3xl mx-auto space-y-5">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">Pagos</h1>
          <p className="text-xs text-slate-400 mt-0.5">Compras, renovaciones y embudo de conversión</p>
        </div>
        <button
          onClick={load}
          className="w-8 h-8 rounded-lg flex items-center justify-center border border-slate-200 dark:border-slate-700 text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
        >
          <span className="material-symbols-outlined text-[18px]">refresh</span>
        </button>
      </div>

      {errorCarga && (
        <div className="rounded-2xl border border-red-300 dark:border-red-500/30 bg-red-50 dark:bg-red-900/20 p-4 flex gap-3">
          <span className="material-symbols-outlined text-[20px] text-red-600 dark:text-red-400 shrink-0">error</span>
          <p className="text-xs text-red-900 dark:text-red-200">{errorCarga}</p>
        </div>
      )}

      {/* Estado de la verificación de pagos */}
      {paypal?.ready && paypal.verifyMode === 'strict' && (
        <div className="rounded-2xl border border-emerald-200 dark:border-emerald-500/25 bg-emerald-50 dark:bg-emerald-900/15 p-3.5 flex items-center gap-3">
          <span className="material-symbols-outlined text-[20px] text-emerald-600 dark:text-emerald-400 shrink-0">verified_user</span>
          <p className="text-xs text-emerald-900 dark:text-emerald-200">
            <span className="font-semibold">Pagos verificados con PayPal.</span> Cada activación se confirma contra
            PayPal y los avisos sin firma válida se descartan.
          </p>
        </div>
      )}
      {!paypal && !errorCarga && (
        <div className="rounded-2xl border border-slate-200 dark:border-white/8 bg-white dark:bg-slate-card p-3.5 flex items-center gap-3">
          <span className="material-symbols-outlined text-[20px] text-slate-400 shrink-0">help</span>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            No se pudo consultar el estado de la verificación de pagos.
          </p>
        </div>
      )}
      {paypal && !paypal.ready && (
        <div className="rounded-2xl border border-amber-300 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-900/20 p-4 flex gap-3">
          <span className="material-symbols-outlined text-[20px] text-amber-600 dark:text-amber-400 shrink-0">warning</span>
          <div className="text-xs text-amber-900 dark:text-amber-200 space-y-1">
            <p className="font-semibold">La verificación de pagos no está completa</p>
            <p className="opacity-90">
              Faltan {[
                !paypal.clientIdConfigured && 'PAYPAL_CLIENT_ID',
                !paypal.clientSecretConfigured && 'PAYPAL_CLIENT_SECRET',
                !paypal.webhookIdConfigured && 'PAYPAL_WEBHOOK_ID',
              ].filter(Boolean).join(', ')} en el servidor.
              {paypal.verifyMode === 'strict'
                ? ' Mientras tanto no se aceptan activaciones ni eventos de PayPal.'
                : ' El modo permisivo está activo: los pagos pasan sin verificar.'}
            </p>
          </div>
        </div>
      )}
      {paypal?.ready && paypal.verifyMode !== 'strict' && (
        <div className="rounded-2xl border border-amber-300 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-900/20 p-4 flex gap-3">
          <span className="material-symbols-outlined text-[20px] text-amber-600 dark:text-amber-400 shrink-0">gpp_maybe</span>
          <p className="text-xs text-amber-900 dark:text-amber-200">
            <span className="font-semibold">Modo permisivo activo.</span> Los pagos se aceptan aunque no puedan verificarse con PayPal.
            Cambia <code>PAYPAL_VERIFY_MODE</code> a <code>strict</code> cuando termines de diagnosticar.
          </p>
        </div>
      )}

      {/* Métricas */}
      {stats && (
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-2xl border border-slate-200 dark:border-white/8 bg-white dark:bg-slate-card p-4">
            <span className="material-symbols-outlined text-[22px] text-emerald-500">payments</span>
            <p className="text-xl font-bold text-slate-800 dark:text-white mt-1">{money(stats.ingresosMes)}</p>
            <p className="text-[11px] text-slate-400 mt-0.5">Cobrado este mes</p>
          </div>
          <div className="rounded-2xl border border-slate-200 dark:border-white/8 bg-white dark:bg-slate-card p-4">
            <span className="material-symbols-outlined text-[22px] text-indigo-500">check_circle</span>
            <p className="text-xl font-bold text-slate-800 dark:text-white mt-1">{stats.pagos + stats.renovaciones}</p>
            <p className="text-[11px] text-slate-400 mt-0.5">Cobros totales</p>
          </div>
          <div className="rounded-2xl border border-slate-200 dark:border-white/8 bg-white dark:bg-slate-card p-4">
            <span className="material-symbols-outlined text-[22px] text-amber-500">conversion_path</span>
            <p className="text-xl font-bold text-slate-800 dark:text-white mt-1">{stats.tasaConversion}%</p>
            <p className="text-[11px] text-slate-400 mt-0.5 flex items-center">
              Conversión
              <Tooltip text={`De ${stats.checkouts} personas que entraron a pagar, ${stats.checkoutsConvertidos} completaron la compra.`} />
            </p>
          </div>
        </div>
      )}

      {/* Abandonos: lo más accionable */}
      {abandonados.length > 0 && (
        <div className="rounded-2xl border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-950/20 p-4">
          <div className="flex items-start gap-3 mb-3">
            <span className="material-symbols-outlined text-[20px] text-amber-500 shrink-0 mt-0.5">shopping_cart_off</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-300">
                {abandonados.length} {abandonados.length === 1 ? 'persona entró a pagar' : 'personas entraron a pagar'} y no {abandonados.length === 1 ? 'terminó' : 'terminaron'}
              </p>
              <p className="text-xs text-amber-700/80 dark:text-amber-400/80 mt-0.5">
                Ya mostraron intención de compra. Escribirles suele ser la conversión más fácil.
              </p>
            </div>
          </div>
          <div className="space-y-2">
            {abandonados.slice(0, 5).map(a => (
              <div key={a.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-white dark:bg-slate-900/40">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 dark:text-white truncate">
                    {a.organizationName || a.buyerName || 'Sin nombre'}
                  </p>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    {a.planLabel || a.plan} · {timeAgo(a.createdAt)}
                  </p>
                </div>
                {a.buyerPhone && (
                  <a
                    href={`https://wa.me/${a.buyerPhone.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold transition-colors shrink-0"
                  >
                    <span className="material-symbols-outlined text-[14px]">chat</span>
                    Escribir
                  </a>
                )}
              </div>
            ))}
            {abandonados.length > 5 && (
              <p className="text-[11px] text-amber-700/70 dark:text-amber-400/70 text-center pt-1">
                y {abandonados.length - 5} más en la lista de abajo
              </p>
            )}
          </div>
        </div>
      )}

      {events.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center gap-2">
          <span className="material-symbols-outlined text-[48px] text-slate-300 dark:text-slate-600">receipt_long</span>
          <p className="text-sm text-slate-500 dark:text-slate-400">Todavía no hay movimientos</p>
          <p className="text-xs text-slate-400 max-w-sm">
            Aquí aparecerán las compras, renovaciones y quienes entren a pagar, desde ahora en adelante.
          </p>
        </div>
      ) : (
        <>
          {/* Filtros */}
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-indigo-500 text-white shadow-sm'
                  : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
              }`}
            >
              Todos ({events.length})
            </button>
            {Object.entries(conteos).sort((a, b) => b[1] - a[1]).map(([t, n]) => (
              <button
                key={t}
                onClick={() => setFilter(t)}
                className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-colors ${
                  filter === t
                    ? 'bg-indigo-500 text-white shadow-sm'
                    : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
                }`}
              >
                {meta(t).label} ({n})
              </button>
            ))}
          </div>

          {/* Lista */}
          <div className="space-y-2">
            {filtered.map(ev => {
              const m = meta(ev.type);
              const isOpen = expanded === ev.id;
              return (
                <div key={ev.id} className="rounded-2xl border border-slate-200 dark:border-white/8 bg-white dark:bg-slate-card overflow-hidden">
                  <button
                    className="w-full text-left px-4 py-3.5 flex items-start gap-3 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
                    onClick={() => setExpanded(isOpen ? null : ev.id)}
                  >
                    <span className={`material-symbols-outlined text-[20px] ${m.color} shrink-0 mt-0.5`}>{m.icon}</span>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full uppercase tracking-wide ${m.badge}`}>
                          {m.label}
                        </span>
                        {ev.isEarlyOffer && (
                          <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full uppercase tracking-wide bg-amber-500 text-white">
                            preferencial
                          </span>
                        )}
                        <span className="text-[11px] text-slate-400">{timeAgo(ev.createdAt)}</span>
                      </div>
                      <p className="text-sm font-semibold text-slate-800 dark:text-white mt-1 truncate">
                        {ev.organizationName || ev.buyerName || 'Sin nombre'}
                      </p>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400">
                        {ev.planLabel || ev.plan || '—'}
                        {ev.billingCycle === 'annual' && ' · anual'}
                      </p>
                    </div>

                    <div className="shrink-0 text-right">
                      <p className={`text-sm font-bold ${ev.type === 'subscription_cancelled' ? 'text-slate-400 line-through' : 'text-slate-800 dark:text-white'}`}>
                        {money(ev.amount)}
                      </p>
                    </div>

                    <span className={`material-symbols-outlined text-[18px] text-slate-300 dark:text-slate-600 transition-transform shrink-0 ${isOpen ? 'rotate-180' : ''}`}>
                      expand_more
                    </span>
                  </button>

                  {isOpen && (
                    <div className="px-4 pb-4 pt-1 border-t border-slate-100 dark:border-white/5 space-y-3">
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div className="min-w-0">
                          <p className="text-[11px] text-slate-400 uppercase tracking-wide">Comprador</p>
                          <p className="text-slate-700 dark:text-slate-200 mt-0.5 truncate">{ev.buyerName || '—'}</p>
                        </div>
                        <div className="min-w-0">
                          <p className="text-[11px] text-slate-400 uppercase tracking-wide">Email</p>
                          <p className="text-slate-700 dark:text-slate-200 mt-0.5 truncate">{ev.buyerEmail || '—'}</p>
                        </div>
                        <div className="min-w-0">
                          <p className="text-[11px] text-slate-400 uppercase tracking-wide">Teléfono</p>
                          <p className="text-slate-700 dark:text-slate-200 mt-0.5">{ev.buyerPhone || '—'}</p>
                        </div>
                        <div className="min-w-0">
                          <p className="text-[11px] text-slate-400 uppercase tracking-wide">Fecha</p>
                          <p className="text-slate-700 dark:text-slate-200 mt-0.5">
                            {new Date(ev.createdAt).toLocaleString('es-DO', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}
                          </p>
                        </div>
                        {(ev.trialDaysUsed != null || ev.contactsUsed != null) && (
                          <div className="col-span-2">
                            <p className="text-[11px] text-slate-400 uppercase tracking-wide">Cómo llegó</p>
                            <p className="text-slate-700 dark:text-slate-200 mt-0.5">
                              {ev.trialDaysUsed != null && `${ev.trialDaysUsed} días de prueba`}
                              {ev.trialDaysUsed != null && ev.contactsUsed != null && ' · '}
                              {ev.contactsUsed != null && `${ev.contactsUsed} contactos atendidos`}
                            </p>
                          </div>
                        )}
                        {ev.paypalSubscriptionId && (
                          <div className="col-span-2 min-w-0">
                            <p className="text-[11px] text-slate-400 uppercase tracking-wide">Suscripción PayPal</p>
                            <p className="text-slate-700 dark:text-slate-200 mt-0.5 font-mono text-[11px] truncate">{ev.paypalSubscriptionId}</p>
                          </div>
                        )}
                      </div>

                      {ev.buyerPhone && (
                        <a
                          href={`https://wa.me/${ev.buyerPhone.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-800 dark:bg-white text-white dark:text-slate-900 text-xs font-semibold hover:opacity-90 transition-all"
                        >
                          <span className="material-symbols-outlined text-[16px]">chat</span>
                          Escribir por WhatsApp
                        </a>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default Payments;
